/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

import {formatIsoCurrency} from '../../../components/util/iso-currency-format';
import {formatPercentage} from './format-percentage';
import {PERCENTAGE_FIELDS, RATIO_FIELDS} from './percent-fields';
import {formatLocaleNumber} from '../locale/locale-number-format';

function outputCurrency(stat: any, fractionSize?: number, defaultCurrency?: string, locale = 'en'): string {
  const currencyValue = formatIsoCurrency(stat.amount, stat.currency, fractionSize, false, false, defaultCurrency, locale);
  const secondaryCurrencyValue = stat.maxAmount
    ? (' to ' + formatIsoCurrency(stat.maxAmount, stat.currency, fractionSize, true, false, defaultCurrency, locale))
    : '';
  return (currencyValue || 0) + secondaryCurrencyValue;
}

export function formatAdnStat(
  stat: any,
  field?: string,
  fractionSize?: number,
  objId?: string,
  defaultCurrency?: string,
  locale = 'en'
): string {
  if (_.isNull(stat) || _.isNaN(stat) || _.isUndefined(stat) || (_.isObject(stat) && _.isEmpty(stat)) || (_.has(stat, 'currency') && !_.isFinite(_.get(stat, 'amount')))) {
    return '-';
  }
  if (objId && _.isObject(stat) && _.has(stat, objId)) {
    const objValue = _.get(stat, objId);
    // A custom event's averageValue can itself be a monetary object ({currency, amount}).
    // Format it the same way the totals table does by passing it back through the currency path.
    if (_.isObject(objValue)) {
      return formatAdnStat(objValue, field, fractionSize, undefined, defaultCurrency, locale);
    }
    if (_.isFinite(objValue)) {
      return formatLocaleNumber(objValue, objId === 'averageValue' ? '1.2-2' : '1.0-0', locale);
    }
  }
  if (_.isFinite(stat)) {
    if (field && PERCENTAGE_FIELDS[field]) {
      return formatPercentage(stat, PERCENTAGE_FIELDS[field].decPlaces, undefined, locale);
    }
    const digits = Number.isInteger(stat) ? 0 : (fractionSize || _.get(RATIO_FIELDS, [field, 'decPlaces']));
    return formatLocaleNumber(stat, _.isFinite(digits) ? `1.${digits}-${digits}` : undefined, locale);
  }

  if (_.isArray(stat)) {
    let output = '';
    for (let i = 0; i < stat.length; i++) {
      output += outputCurrency(stat[i], fractionSize, defaultCurrency, locale) + (i === stat.length - 1 ? ' ' : ', ');
    }
    return output;
  }
  if (_.has(stat, 'currency')) {
    return outputCurrency(stat, fractionSize, defaultCurrency, locale);
  }
  return stat.toString();
}
